/**
 * Hardware Types hook using React Relay
 *
 * Fetches hardware types for dropdowns and filters.
 *
 * Note: Run `npm run relay` after making changes to GraphQL queries
 * to regenerate the type artifacts.
 */

import { useLazyLoadQuery, fetchQuery } from 'react-relay';
import { useState, useEffect, useCallback, useMemo } from 'react';
import graphql from 'babel-plugin-relay/macro';
import { relayEnvironment } from '../../../../relay';
import type { useHardwareTypesRelayQuery as UseHardwareTypesRelayQuery } from './__generated__/useHardwareTypesRelayQuery.graphql';

// ============================================================================
// GraphQL Definitions
// ============================================================================

/**
 * Query for hardware types list
 */
const HardwareTypesQuery = graphql`
  query useHardwareTypesRelayQuery($first: Int) {
    listHardwareTypes(first: $first, sort: [{ field: NAME, order: ASC }]) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`;

// ============================================================================
// Types
// ============================================================================

/**
 * Hardware Type from GraphQL
 */
export interface HardwareTypeGraphQL {
  id: string;
  name?: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for fetching hardware types using Relay with Suspense
 *
 * Uses Suspense - wrap with Suspense boundary
 *
 * @example
 * function HardwareTypeSelect() {
 *   return (
 *     <Suspense fallback={<Loading />}>
 *       <HardwareTypeSelectContent />
 *     </Suspense>
 *   );
 * }
 *
 * function HardwareTypeSelectContent() {
 *   const types = useHardwareTypesRelay();
 *   return <Select options={types.map(t => ({ value: t.id, label: t.name }))} />;
 * }
 */
export function useHardwareTypesRelay(options?: { limit?: number }) {
  const data = useLazyLoadQuery<UseHardwareTypesRelayQuery>(HardwareTypesQuery, {
    first: options?.limit ?? 100,
  });

  const types =
    data.listHardwareTypes?.edges?.map((edge) => edge?.node).filter(Boolean) ?? [];

  return types as HardwareTypeGraphQL[];
}

// ============================================================================
// Non-Suspense Hook (with loading state)
// ============================================================================

/**
 * Hook that provides React Query-like interface for hardware types
 *
 * This hook does NOT use Suspense - it provides isLoading, data, refetch etc.
 *
 * @example
 * const { data, isLoading } = useHardwareTypesRelayQuery();
 *
 * if (isLoading) return <Loading />;
 * return <Select options={data.map(t => ({ value: t.id, label: t.name }))} />;
 */
export function useHardwareTypesRelayQuery(options?: {
  enabled?: boolean;
  limit?: number;
}) {
  const [data, setData] = useState<HardwareTypeGraphQL[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetched, setIsFetched] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [fetchCount, setFetchCount] = useState(0);

  const enabled = options?.enabled !== false;
  const limit = options?.limit ?? 100;

  useEffect(() => {
    if (!enabled) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const doFetch = async () => {
      try {
        setIsLoading(true);

      const result = await fetchQuery<UseHardwareTypesRelayQuery>(
          relayEnvironment,
          HardwareTypesQuery,
          { first: limit }
        ).toPromise();

        if (cancelled) return;

        if (result) {
          const types =
            result.listHardwareTypes?.edges?.map((edge) => edge?.node).filter(Boolean) ??
            [];
          setData(types as HardwareTypeGraphQL[]);
          setError(null);
        }
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err : new Error('Failed to fetch hardware types')
        );
      } finally {
        if (!cancelled) {
          setIsLoading(false);
          setIsFetched(true);
        }
      }
    };

    doFetch();

    return () => {
      cancelled = true;
    };
  }, [enabled, limit, fetchCount]);

  const refetch = useCallback(() => {
    setFetchCount((c) => c + 1);
  }, []);

  return useMemo(
    () => ({
      data,
      isLoading,
      isFetched,
      error,
      refetch,
    }),
    [data, isLoading, isFetched, error, refetch]
  );
}
